import React, { useEffect, useState, useCallback } from 'react';
import { axiosInstance } from "../../utils/axios.js";
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, CheckCircle, Upload, FileText, AlertTriangle, X, CreditCard } from 'lucide-react';
import './reserve.scss';

// マークダウン形式のリンク [テキスト](URL) をReact要素に変換する関数
const parseMarkdownLinks = (text) => {
    if (!text) return text;
    
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = linkRegex.exec(text)) !== null) {
        // リンク前のテキストを追加
        if (match.index > lastIndex) {
            parts.push(text.substring(lastIndex, match.index));
        }
        
        // リンクを追加
        parts.push(
            <a 
                key={match.index}
                href={match[2]} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: '#3b82f6', textDecoration: 'underline' }}
            >
                {match[1]}
            </a>
        );
        
        lastIndex = match.index + match[0].length;
    }
    
    // 残りのテキストを追加
    if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
    }
    
    return parts.length > 0 ? parts : text;
};

// マイナ保険証情報・資格確認書変更確認の質問IDリスト
const INSURANCE_CHANGE_QUESTION_IDS = [44, 45, 46, 47];

// 前回のマイナ保険証情報・資格確認書を表示するコンポーネント
const PreviousInsuranceDisplay = ({ token }) => {
  const [previousInsurance, setPreviousInsurance] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const fetchPreviousInsurance = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/users/latest_insurance_info', {
        headers: {
            'uid': token,
            'Access-Control-Allow-Origin': '*'
        }
      });
      setPreviousInsurance(response.data);
      console.log(response.data)
    } catch (error) {
      console.error('マイナ保険証情報・資格確認書の取得に失敗しました', error);
    } finally {
      setLoading(false);
    }
  }, [token]);
  
  useEffect(() => {
    fetchPreviousInsurance();
  }, [fetchPreviousInsurance]);
  
  if (loading) return <div className="loading">読み込み中...</div>;
  
  if (!previousInsurance) {
    return (
      <div className="no-previous-insurance-alert">
        <AlertTriangle className="alert-icon" />
        <p>前回のマイナ保険証情報・資格確認書が見つかりません</p>
      </div>
    );
  }
  
  return (
    <div className="previous-insurance-display">
      <h4 className="previous-insurance-title">
        前回ご提出いただいたマイナ保険証情報・資格確認書
      </h4>
      <div className="insurance-info-grid">
        <div className="insurance-images">
          {previousInsurance.images.map((img, idx) => (
            <div key={idx} className="insurance-image-wrapper">
              <img 
                src={img.url} 
                alt={`マイナ保険証情報・資格確認書${idx === 0 ? '表面' : '裏面'}`}
                className="insurance-image"
                style={{width:'90%'}}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ReDiagnoseContainer = (props) => {
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState({});
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const { id } = useParams();
    const location = useLocation();
    const [questionnaireId, setQuestionnaireId] = useState(null);
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);
    const [cancelModal, setCancelModal] = useState(false);
    const [deadlineModal, setDeadlineModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [shiftInfo, setShiftInfo] = useState(null);
    const [last_name, setLastName] = useState('');

    useEffect(() => {
        // 診察情報を先に設定
        const adminUserId = location.state?.admin_user_id;
        const startTime = location.state?.start_time;
        const last_name = location.state?.last_name;
        
        // 問診情報を取得
        axiosInstance.get(`/reservations/rediagnose?shift_id=${location.state.id}&subject_id=${location.state.subject_id}`, {
            headers: {
                'uid': props.token,
                'Access-Control-Allow-Origin': '*'
            }
        })
        .then(response => {
            const fetchedQuestions = response.data.questions;
            setQuestions(fetchedQuestions);
            
            const initialAnswers = {};
            fetchedQuestions.forEach(question => {
                if (question.question_type === "image_upload") {
                    initialAnswers[question.id] = null;
                } else {
                    initialAnswers[question.id] = "";
                }
            });
            
            setAnswers(initialAnswers);
            if (fetchedQuestions.length > 0) {
                setQuestionnaireId(fetchedQuestions[0].questionnaire_id);
            }
        })
        .catch(error => console.error(error));
    }, [id, location.state, props.token]);

    const handleAnswerChange = (questionId, answer) => {
        setAnswers({
            ...answers,
            [questionId]: answer
        });
    };

    const handleMultipleChoiceChange = (questionId, choiceId, checked) => {
        setAnswers(prevAnswers => {
            const currentAnswers = prevAnswers[questionId] || [];
            return {
                ...prevAnswers,
                [questionId]: checked 
                    ? [...currentAnswers, choiceId] 
                    : currentAnswers.filter(id => id !== choiceId)
            };
        });
    };

    const handleImageUpload = (questionId, file) => {
        if (!file) return;
        setAnswers(prev => ({
            ...prev,
            [questionId]: file
        }));
    };

    const scrollToTop = () => {
        const sunnyContainer = document.querySelector('.sunny_container');
        if (sunnyContainer) {
            sunnyContainer.scrollTo({ top: 0, behavior: 'smooth' });
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        
        // start_timeフォーマット（例: "2025-04-03T23:40:00"）の場合
        if (dateString.includes('T')) {
            const date = new Date(dateString);
            const year = date.getFullYear();
            const month = date.getMonth() + 1;
            const day = date.getDate();
            const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
            const weekday = weekdays[date.getDay()];
            return `${year}年${month}月${day}日（${weekday}）`;
        }
        
        // 通常の日付フォーマット
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
        const weekday = weekdays[date.getDay()];
        return `${year}年${month}月${day}日（${weekday}）`;
    };

    const formatTime = (timeString) => {
        if (!timeString) return '';
        
        // start_timeフォーマット（例: "2025-04-03T23:40:00"）の場合
        if (timeString.includes('T')) {
            const date = new Date(timeString);
            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');
            return `${hours}:${minutes}`;
        }
        
        // HH:MM:SS形式の場合
        return timeString.substring(0, 5);
    };

    const handleConfirmReservation = () => {
        setShowConfirmation(true);
        setTimeout(scrollToTop, 50);
    };

    const handleBackFromConfirmation = () => {
        setShowConfirmation(false);
        // 最後の質問ページに戻る
        const filteredQuestions = questions.filter(q => q.id !== 20 && q.id !== 21);
        setCurrentQuestionIndex(filteredQuestions.length - 1);
    };
    
    const handleSubmit = () => {
        setIsLoading(true);
        const formData = new FormData();
    
        Object.keys(answers).forEach(questionId => {
            if (answers[questionId] instanceof File) {
                formData.append(`images[${questionId}]`, answers[questionId]);
            } else if (answers[questionId] !== null) {
                formData.append(`answers[${questionId}]`, answers[questionId]);
            }
        });
    
        formData.append('questionnaire_id', questionnaireId);

        const shiftIdsParam = location.state.shiftIds
        .map(id => `shift_ids[]=${id}`)
        .join('&');
    
        axiosInstance.post(`/reservations/create_through_rediagnose/${shiftIdsParam}?subject_id=${location.state.subject_id}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                'uid': props.token,
                'Access-Control-Allow-Origin': '*'
            },  
            params: {
                shift_ids: location.state.shiftIds,
                subject_id: location.state.subject_id
            }
        })
        .then(res => {
            setIsLoading(false);
            if (res.data.message === '1') {
                setShowModal(true);
            } else if (res.data.message === '3') {
                setCancelModal(true);
            } else if (res.data.message === '4') {
                setDeadlineModal(true);
            } else {
                navigate('/mypage/reservation?status=success');
            }
        })
        .catch(error => console.error(error));
    };

    const closeModal = () => {
        setShowModal(false);
        navigate('/mypage/reservation');
    };
    
    const closeModal_2 = () => {
        setCancelModal(false);
        navigate('/mypage/reservation');
    };

    // 現在のページで表示する質問を取得
    const getCurrentQuestions = () => {
        // 保険証アップロード質問（20, 21）を除外
        const filteredQuestions = questions.filter(q => q.id !== 20 && q.id !== 21);
        
        return filteredQuestions[currentQuestionIndex] ? [filteredQuestions[currentQuestionIndex]] : [];
    };

    const isAnswerProvided = () => {
        const currentQuestions = getCurrentQuestions();
        
        return currentQuestions.every(question => {
            if (!question.required) return true;
            
            switch (question.question_type) {
                case 'multiple_choice':
                    return answers[question.id]?.length > 0;
                case 'single_choice':
                    return answers[question.id] !== undefined && answers[question.id] !== "";
                case 'image_upload':
                    return answers[question.id] instanceof File;
                default:
                    return answers[question.id]?.toString().trim() !== "";
            }
        });
    };

    const goToNextQuestion = () => {
        const filteredQuestions = questions.filter(q => q.id !== 20 && q.id !== 21);
        const totalPages = filteredQuestions.length;
            
        if (currentQuestionIndex < totalPages - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        }
    };

    const goToPreviousQuestion = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        }
    };

    const getQuestionPlaceholder = (question) => {
        const placeholdersByQuestionId = {
            // 基本情報（再診でも使う可能性がある）
            1: "山田太郎",
            2: "やまだたろう",
            3: "example@email.com",
            4: "09012345678（ハイフンなし）",
            5: "生年月日を選択してください",
            9: "123-4567（ハイフンあり）",
            10: "東京都渋谷区○○1-2-3",
            11: "〇〇マンション 101号室",
            14: "例：降圧薬（アムロジピン）、糖尿病薬（メトホルミン）など\n服用していない場合は「なし」と記入",
            
            // 再診特有の質問
            44: "マイナ保険証情報・資格確認書に変更がある場合は選択してください",
            45: "変更内容を具体的にご記入ください",
            46: "新しいマイナ保険証情報・資格確認書番号をご記入ください",
            47: "保険者番号をご記入ください",
        };

        const contentBasedPlaceholders = {
            "氏名": "山田太郎",
            "ふりがな": "やまだたろう",
            "メールアドレス": "example@email.com",
            "電話番号": "09012345678（ハイフンなし）",
            "郵便番号": "123-4567（ハイフンあり）",
            "住所": "都道府県から番地まで入力してください",
            "建物名": "マンション名・アパート名と部屋番号",
            "身長": "170（単位不要）",
            "体重": "65（単位不要）",
            "服薬中": "現在服用中のお薬をすべてご記入ください。ない場合は「なし」",
            "症状": "現在の症状を詳しくご記入ください",
            "変更": "変更内容を具体的にご記入ください",
            "マイナ保険証情報・資格確認書": "マイナ保険証情報・資格確認書番号をご記入ください",
        };

        const typeBasedPlaceholders = {
            text: "詳しくご記入ください",
            sentence: "ご記入ください",
            number: "数値を入力してください",
            date: "日付を選択してください",
        };

        if (placeholdersByQuestionId[question.id]) {
            return placeholdersByQuestionId[question.id];
        }

        for (const [keyword, placeholder] of Object.entries(contentBasedPlaceholders)) {
            if (question.content && question.content.includes(keyword)) {
                return placeholder;
            }
        }

        return typeBasedPlaceholders[question.question_type] || "ご記入ください";
    };

    // 総ページ数の計算
    const filteredQuestions = questions.filter(q => q.id !== 20 && q.id !== 21);
    const totalPages = filteredQuestions.length;
    const progressPercentage = totalPages > 0 ? ((currentQuestionIndex + 1) / totalPages) * 100 : 0;

    if (isLoading) {
        return (
          <div>
            <div className="loader" style={{ width: '100px', height: '100px' }}></div>
            <div
              style={{
                width: '100%',
                position: 'absolute',
                bottom: '0%',
                height: '80px',
                background: '#FEFBF9',
                zIndex: '12',
              }}
            />
          </div>
        );
    }

    // 確認画面の表示
    if (showConfirmation) {
        return (
            <div className="sunny_container">
                <div className="diagnose_main_wrapper">
                    <div className="reservation_confirmation_container">
                        <div className="confirmation_header">
                            <h2 className="confirmation_title">以下の内容で予約確定しますか？</h2>
                        </div>
                        
                        <div className="confirmation_content">
                            <div className="confirmation_section">
                                <div className="confirmation_card">
                                <h3 className="section_subtitle">ご予約内容</h3>
                                    <div className="doctor_info">
                                        <div className="doctor_details">
                                            <h4 className="doctor_name">担当医: {location.state?.last_name}先生</h4>
                                            <p className="appointment_datetime">
                                                {formatDate(shiftInfo?.start_time || location.state?.start_time)}
                                                {formatTime(shiftInfo?.start_time || location.state?.start_time)}〜
                                            </p>
                                            <p className="appointment_duration">診察時間目安：約10分</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="payment_section">
                                <h3 className="section_subtitle">お支払い料金の目安</h3>
                                <div className="payment_card">
                                    <div className="payment_row">
                                        <span className="payment_label">再診</span>
                                        <div>
                                            <span className="payment_amount" style={{fontSize:'22px',paddingRight:'6px'}}>2,500</span>
                                            <span className="payment_amount">円前後（税込）</span>
                                            <span className="payment_amount">+ お薬代</span>
                                        </div>
                                    </div>
                                    <div className="payment_notes">
                                        <p className="payment_note">お支払い方法はクレジットカード・銀行振込が利用できます。</p>
                                        <p className="payment_note">目安の金額になります。保険診療のため、診察内容に応じて料金が異なりますので、ご注意ください。</p>
                                        <p className="payment_note">薬代は処方日数や薬の種類によって値段が変動します。</p>
                                        <p className="payment_note">追加で血液検査キットをご利用の場合は自費負担（5,940円）となります。</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="confirmation_actions">
                            <button 
                                onClick={handleBackFromConfirmation}
                                className="confirmation_button back"
                            >
                                戻る
                            </button>
                            <button 
                                onClick={handleSubmit}
                                className="confirmation_button confirm analytics_conversion_reservation_followup"
                            >
                                予約する
                            </button>
                        </div>
                    </div>
                </div>
                                                {/* モーダル群 */}
                                                {showModal && (
                    <div className="modal-overlay" onClick={closeModal}>
                        <div className="modal-content warning_modal" onClick={(e) => e.stopPropagation()}>
                            <span className="close-button" onClick={closeModal}>
                                <X />
                            </span>
                            <div className="modal_icon warning_icon">
                                <AlertTriangle />
                            </div>
                            <p className="modal_title">予約の競合が発生しました</p>
                            <p className="modal_subtitle">同じ時間の予約が先に埋まってしまいました。大変お手数ですが再度予約をお取りください。</p>
                        </div>
                    </div>
                )}
                
                {cancelModal && (
                    <div className="modal-overlay" onClick={() => closeModal_2()}>
                        <div className="modal-content warning_modal" onClick={(e) => e.stopPropagation()}>
                            <span className="close-button" onClick={() => closeModal_2()}>
                                <X />
                            </span>
                            <div className="modal_icon warning_icon">
                                <AlertTriangle />
                            </div>
                            <p className="modal_title">予約の重複が検出されました</p>
                            <p className="modal_subtitle">お客様の予約が重複しております。すでに診察完了前の予約が入っておりますのでそちらをキャンセルもしくは予約時間変更してご利用ください。</p>
                        </div>
                    </div>
                )}
                
                {deadlineModal && (
                    <div className="modal-overlay" onClick={() => closeModal_2()}>
                        <div className="modal-content warning_modal" onClick={(e) => e.stopPropagation()}>
                            <span className="close-button" onClick={() => closeModal_2()}>
                                <X />
                            </span>
                            <div className="modal_icon warning_icon">
                                <AlertTriangle />
                            </div>
                            <p className="modal_title">予約締切時間を過ぎました</p>
                            <p className="modal_subtitle">予約締切時間が過ぎました。お手数ですが診察開始時間以内に再度ご予約をお取りください。</p>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="sunny_container">
            <div className="diagnose_main_wrapper rediagnose">
                {/* プログレスバー */}
                <div className="diagnose_progress_container">
                    <div className="diagnose_progress_wrapper">
                        <div className="diagnose_progress_bar">
                            <div 
                                className="diagnose_progress_fill rediagnose"
                                style={{ width: `${progressPercentage}%` }}
                            />
                        </div>
                        <span className="diagnose_progress_text">
                            {currentQuestionIndex + 1} / {totalPages}
                        </span>
                    </div>
                </div>

                {/* 質問コンテンツ */}
                <div className="diagnose_questions_container">
                    {getCurrentQuestions().length > 0 ? (
                        getCurrentQuestions().map((question) => {
                            // マイナ保険証情報・資格確認書変更確認の質問の場合
                            if (INSURANCE_CHANGE_QUESTION_IDS.includes(question.id)) {
                                return (
                                    <div key={question.id} className="diagnose_question_card insurance-change-special">                                        
                                        <div className="diagnose_question_header" style={{display:'flex',flexDirection:'row',alignItems:'center'}}>
                                            <CreditCard className="diagnose_question_icon" />
                                            <h3 className="diagnose_question_title">
                                                {question.content}
                                                {question.required && <span className="diagnose_required_mark">※必須</span>}
                                            </h3>
                                        </div>
                                        
                                        <div className="diagnose_question_content">
                                            <div className="diagnose_choices_wrapper">
                                                {question.choices.map(choice => (
                                                    <label
                                                        key={choice.id}
                                                        className={`diagnose_choice_option single rediagnose ${answers[question.id] === choice.id ? 'selected' : ''}`}
                                                        onClick={() => handleAnswerChange(question.id, choice.id)}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name={`question-${question.id}`}
                                                            value={choice.id}
                                                            checked={answers[question.id] === choice.id}
                                                            onChange={() => handleAnswerChange(question.id, choice.id)}
                                                            className="diagnose_choice_input"
                                                        />
                                                        <div className="diagnose_choice_indicator rediagnose">
                                                            {answers[question.id] === choice.id && <CheckCircle className="diagnose_choice_check" />}
                                                        </div>
                                                        <span className="diagnose_choice_text">{choice.choice_text}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                        <PreviousInsuranceDisplay token={props.token} />
                                    </div>
                                );
                            }
                            
                            // 通常の質問
                            return (
                                <div 
                                    key={question.id} 
                                    className={`diagnose_question_card rediagnose`}
                                >
                                    <div className="diagnose_question_header" style={{display:'flex',flexDirection:'row',alignItems:'center'}}>
                                        <FileText className="diagnose_question_icon rediagnose" />
                                        <h3 className={`diagnose_question_title`} style={{textAlign:'left'}}>
                                            {parseMarkdownLinks(question.content)}
                                            {question.required && <span className="diagnose_required_mark">※必須</span>}
                                        </h3>
                                    </div>
                                    
                                    <div className="diagnose_question_content">
                                        {question.question_type === 'single_choice' && (
                                            <div className="diagnose_choices_wrapper">
                                                {question.choices.map(choice => (
                                                    <label
                                                        key={choice.id}
                                                        className={`diagnose_choice_option single rediagnose ${answers[question.id] === choice.id ? 'selected' : ''}`}
                                                        onClick={() => handleAnswerChange(question.id, choice.id)}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name={`question-${question.id}`}
                                                            value={choice.id}
                                                            checked={answers[question.id] === choice.id}
                                                            onChange={() => handleAnswerChange(question.id, choice.id)}
                                                            className="diagnose_choice_input"
                                                        />
                                                        <div className="diagnose_choice_indicator rediagnose">
                                                            {answers[question.id] === choice.id && <CheckCircle className="diagnose_choice_check" />}
                                                        </div>
                                                        <span className="diagnose_choice_text">{choice.choice_text}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}

                                        {question.question_type === 'multiple_choice' && (
                                            <div className="diagnose_choices_wrapper">
                                                {question.choices.map(choice => (
                                                    <label
                                                        key={choice.id}
                                                        className={`diagnose_choice_option multiple rediagnose ${answers[question.id]?.includes(choice.id) ? 'selected' : ''}`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            value={choice.id}
                                                            checked={answers[question.id]?.includes(choice.id) || false}
                                                            onChange={(e) => handleMultipleChoiceChange(question.id, choice.id, e.target.checked)}
                                                            className="diagnose_choice_input"
                                                        />
                                                        <div className="diagnose_choice_indicator rediagnose">
                                                            {answers[question.id]?.includes(choice.id) && <CheckCircle className="diagnose_choice_check" />}
                                                        </div>
                                                        <span className="diagnose_choice_text">{choice.choice_text}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}

                                        {question.question_type === 'text' && (
                                                <div className="diagnose_text_input_wrapper rediagnose">
                                                    <textarea
                                                        value={answers[question.id] || ''}
                                                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                                                        className="diagnose_textarea rediagnose"
                                                        placeholder={getQuestionPlaceholder(question)}
                                                    />
                                                </div>
                                            )}
    
                                        {question.question_type === 'sentence' && (
                                            <div className="diagnose_text_input_wrapper rediagnose">
                                                <textarea
                                                    value={answers[question.id] || ''}
                                                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                                                    className="diagnose_text_input rediagnose"
                                                    placeholder={getQuestionPlaceholder(question)}
                                                />
                                            </div>
                                        )}
                                                                            
                                        {question.question_type === 'number' && (
                                            <div className="diagnose_text_input_wrapper rediagnose">
                                                <FileText className="diagnose_text_icon" />
                                                <input
                                                    type="number"
                                                    value={answers[question.id] || ''}
                                                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                                                    className="diagnose_number_input rediagnose"
                                                    placeholder={getQuestionPlaceholder(question)}
                                                />
                                            </div>
                                        )}
                                                                            
                                        {question.question_type === 'image_upload' && (
                                            <div className={`diagnose_image_upload_wrapper`}>
                                                <div
                                                    className={`diagnose_image_upload_area rediagnose`}
                                                    onClick={() => document.getElementById(`file-input-${question.id}`).click()}
                                                >
                                                    <input
                                                        id={`file-input-${question.id}`}
                                                        type="file"
                                                        accept="image/*,application/pdf"
                                                        style={{ display: 'none' }}
                                                        onChange={(e) => handleImageUpload(question.id, e.target.files[0])}
                                                    />
                                                    {answers[question.id] instanceof File ? (
                                                        <div className="diagnose_image_preview">
                                                            <img
                                                                src={URL.createObjectURL(answers[question.id])}
                                                                alt="preview"
                                                                className="diagnose_preview_image"
                                                            />
                                                            <div className="diagnose_image_overlay rediagnose">
                                                                <CheckCircle className="diagnose_upload_success_icon" />
                                                                <span>画像がアップロードされました</span>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="diagnose_upload_placeholder">
                                                            <Upload className="diagnose_upload_icon" />
                                                            <p className="diagnose_upload_text">
                                                                ここをクリックして画像を添付してください
                                                            </p>
                                                            <p className="diagnose_upload_subtext">
                                                                JPG, PNG, PDF対応
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="diagnose_no_questions">
                            <AlertTriangle className="diagnose_no_questions_icon" />
                            <p>質問がありません。</p>
                        </div>
                    )}
                </div>

                {/* ナビゲーションボタン */}
                <div className="footer_content_page">
                    <div className="diagnose_navigation_buttons">
                        {currentQuestionIndex > 0 && (
                            <button
                                onClick={goToPreviousQuestion}
                                className="diagnose_nav_button previous rediagnose"
                                style={{width:'20%'}}
                            >
                                <ChevronLeft className="diagnose_nav_icon" />
                                <span>戻る</span>
                            </button>
                        )}
                        {currentQuestionIndex < totalPages - 1 ? (
                            <button
                                onClick={goToNextQuestion}
                                className={`diagnose_nav_button next rediagnose ${isAnswerProvided() ? 'active' : 'disabled'}`}
                                disabled={!isAnswerProvided()}
                                style={{width:'80%'}}
                            >
                                <span>次へ進む</span>
                                <ChevronRight className="diagnose_nav_icon" />
                            </button>
                        ) : (
                            <button
                                onClick={handleConfirmReservation}
                                className={`diagnose_nav_button submit rediagnose ${isAnswerProvided() ? 'active' : 'disabled'}`}
                                disabled={!isAnswerProvided()}  
                                style={{width:'80%'}}                            
                            >
                                <span>確認画面へ</span>
                                <ChevronRight className="diagnose_nav_icon" />
                            </button>
                        )}
                    </div>
                </div>
                                {/* モーダル群 */}
                                {showModal && (
                    <div className="modal-overlay" onClick={closeModal}>
                        <div className="modal-content warning_modal" onClick={(e) => e.stopPropagation()}>
                            <span className="close-button" onClick={closeModal}>
                                <X />
                            </span>
                            <div className="modal_icon warning_icon">
                                <AlertTriangle />
                            </div>
                            <p className="modal_title">予約の競合が発生しました</p>
                            <p className="modal_subtitle">同じ時間の予約が先に埋まってしまいました。大変お手数ですが再度予約をお取りください。</p>
                        </div>
                    </div>
                )}
                
                {cancelModal && (
                    <div className="modal-overlay" onClick={() => closeModal_2()}>
                        <div className="modal-content warning_modal" onClick={(e) => e.stopPropagation()}>
                            <span className="close-button" onClick={() => closeModal_2()}>
                                <X />
                            </span>
                            <div className="modal_icon warning_icon">
                                <AlertTriangle />
                            </div>
                            <p className="modal_title">予約の重複が検出されました</p>
                            <p className="modal_subtitle">お客様の予約が重複しております。すでに診察完了前の予約が入っておりますのでそちらをキャンセルもしくは予約時間変更してご利用ください。</p>
                        </div>
                    </div>
                )}
                
                {deadlineModal && (
                    <div className="modal-overlay" onClick={() => closeModal_2()}>
                        <div className="modal-content warning_modal" onClick={(e) => e.stopPropagation()}>
                            <span className="close-button" onClick={() => closeModal_2()}>
                                <X />
                            </span>
                            <div className="modal_icon warning_icon">
                                <AlertTriangle />
                            </div>
                            <p className="modal_title">予約締切時間を過ぎました</p>
                            <p className="modal_subtitle">予約締切時間が過ぎました。お手数ですが診察開始時間以内に再度ご予約をお取りください。</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ReDiagnoseContainer;